import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar as CalendarIcon, DollarSign, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useContacts } from "@/hooks/useContacts";

const opportunityStages = ["Qualified", "Proposal", "Negotiation", "Closed Won", "Closed Lost"];

interface OpportunityFormValues {
  name: string;
  description: string;
  stage: string;
  value: number | null;
  expected_close_date: Date | null;
  organization_id?: string;
  contact_id?: string;
  probability?: number;
  owner_id?: string;
}

interface EnhancedOpportunityFormProps {
  initialValues?: Partial<OpportunityFormValues>;
  onSubmit: (values: OpportunityFormValues) => Promise<void>;
  isSubmitting?: boolean;
  submitLabel?: string;
}

export function EnhancedOpportunityForm({
  initialValues,
  onSubmit,
  isSubmitting = false,
  submitLabel = "Save Opportunity"
}: EnhancedOpportunityFormProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [value, setValue] = useState("");
  const [stage, setStage] = useState("");
  const [probability, setProbability] = useState("");
  const [expectedCloseDate, setExpectedCloseDate] = useState<Date | null>(null);
  const [organizationId, setOrganizationId] = useState<string | undefined>(undefined);
  const [contactId, setContactId] = useState<string | undefined>(undefined);
  
  const { organizations, isLoading: orgsLoading } = useOrganizations();
  const { contacts, isLoading: contactsLoading } = useContacts();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Initialize form with initial values if provided
  useEffect(() => {
    if (initialValues) {
      setName(initialValues.name || "");
      setDescription(initialValues.description || "");
      setStage(initialValues.stage || "");
      setValue(initialValues.value ? initialValues.value.toString() : "");
      setProbability(initialValues.probability ? initialValues.probability.toString() : "");
      
      if (initialValues.expected_close_date) {
        setExpectedCloseDate(new Date(initialValues.expected_close_date));
      }
      
      setOrganizationId(initialValues.organization_id);
      setContactId(initialValues.contact_id);
    }
  }, [initialValues]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name || !stage) {
      return; // Form validation should handle this
    }
    
    const formValues: OpportunityFormValues = {
      name,
      description,
      stage,
      value: value ? parseFloat(value) : null,
      expected_close_date: expectedCloseDate,
      probability: probability ? parseInt(probability) : undefined,
      ...(organizationId && { organization_id: organizationId }),
      ...(contactId && { contact_id: contactId }),
      owner_id: user?.id,
    };
    
    await onSubmit(formValues);
  };

  // Filter contacts by organization if an organization is selected
  const filteredContacts = contactId 
    ? contacts 
    : organizationId 
      ? contacts.filter(contact => contact.organization_id === organizationId)
      : contacts;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div>
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Opportunity name"
            required
          />
        </div>
        
        <div>
          <Label htmlFor="stage">Stage *</Label>
          <Select value={stage} onValueChange={setStage} required>
            <SelectTrigger>
              <SelectValue placeholder="Select opportunity stage" />
            </SelectTrigger>
            <SelectContent>
              {opportunityStages.map((stageOption) => (
                <SelectItem key={stageOption} value={stageOption}>
                  {stageOption}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="value">Value</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="value"
                type="number"
                min="0"
                step="0.01"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="0.00"
                className="pl-9"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="probability">Win Probability (%)</Label>
            <Input
              id="probability"
              type="number"
              min="0"
              max="100"
              value={probability}
              onChange={(e) => setProbability(e.target.value)}
              placeholder="Win probability (0-100)"
            />
          </div>
        </div>
        
        <div>
          <Label htmlFor="expectedCloseDate">Expected Close Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                id="expectedCloseDate"
                type="button"
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !expectedCloseDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {expectedCloseDate ? format(expectedCloseDate, "PPP") : "Select date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={expectedCloseDate || undefined}
                onSelect={(date) => setExpectedCloseDate(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="organization">Related Organization</Label>
            <Select 
              value={organizationId || "none"} 
              onValueChange={(value) => {
                const orgId = value === "none" ? undefined : value;
                setOrganizationId(orgId);
                // Reset contact if changing org
                if (orgId !== organizationId) {
                  setContactId(undefined);
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select organization" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {orgsLoading ? (
                  <div className="flex items-center justify-center p-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                ) : (
                  organizations.map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="contact">Related Contact</Label>
            <Select 
              value={contactId || "none"} 
              onValueChange={(value) => {
                const ctId = value === "none" ? undefined : value;
                setContactId(ctId);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select contact" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {contactsLoading ? (
                  <div className="flex items-center justify-center p-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                ) : (
                  filteredContacts.map((contact) => (
                    <SelectItem key={contact.id} value={contact.id}>
                      {contact.first_name} {contact.last_name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add details about this opportunity"
            className="min-h-32"
          />
        </div>
      </div>
      
      <div className="flex justify-end space-x-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate(-1)}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </div>
    </form>
  );
}